import { useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import Icon from '@/common/components/IconSvg/Icon';
import RingBufferDiagram from '@/resources/icons/ring-buffer-diagram.svg?react';
import useTypedLocalStorage from '@/common/customHooks/useTypedLocalStorage';
import { LocalStorageGlobalObject } from '@/common/types';
import { RING_BUFFER_HELP_SECTION_LOCAL_STORAGE_KEY } from '@/containers/HubDevicesPage/constants';

const AVAILABLE_OPTIONS = [
  {
    title: 'Upload historical data.',
    description:
      'Upload data currently stored in the buffer according to the first-in, first-out (FIFO) principle. The oldest data is uploaded first.',
  },
  {
    title: 'Upload upcoming data.',
    description:
      'Send newly generated data directly to the cloud during the selected upload period.',
  },
  {
    title: 'Upload historical and upcoming data.',
    description:
      'Upload buffered data first, then continue with newly generated data during the selected upload period.',
  },
];

const RingBufferInfoSection = () => {
  const { getObject, updateObjectProperty } = useTypedLocalStorage<
    LocalStorageGlobalObject['ringBufferInfoSection']
  >(RING_BUFFER_HELP_SECTION_LOCAL_STORAGE_KEY);

  const [expanded, setExpanded] = useState(() => getObject()?.expanded ?? true);

  const toggleExpanded = () =>
    setExpanded((prev) => {
      const next = !prev;
      updateObjectProperty('expanded', next);

      return next;
    });

  return (
    <Stack
      data-testid="ring-buffer-help-section"
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        gap: expanded ? theme.lelySpacing.SM : 0,
        borderRadius: theme.lelyBorderRadius.SM,
        border: `1px solid ${theme.palette.border.default}`,
        padding: `${theme.lelySpacing.SM} ${theme.lelySpacing.MD} ${theme.lelySpacing.SM} ${theme.lelySpacing.LG}`,
      })}
    >
      <Box
        onClick={toggleExpanded}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '24px',
          cursor: 'pointer',
        }}
      >
        <Typography
          variant="xs"
          sx={(theme) => ({
            fontFamily: theme.lelyFontFamily.NOTO,
            fontWeight: theme.typography.fontWeightBold,
          })}
        >
          How does the buffer work?
        </Typography>

        <IconButton
          size="small"
          aria-label={expanded ? 'Collapse details' : 'Expand details'}
          onClick={(event) => {
            event.stopPropagation();
            toggleExpanded();
          }}
        >
          <Icon
            name={expanded ? 'lely-icon-caret-up' : 'lely-icon-caret-down'}
            style={{ width: 20, height: 20 }}
          />
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box
          sx={(theme) => ({
            display: 'flex',
            gap: theme.lelySpacing.SM,
          })}
        >
          <Stack>
            <Typography
              variant="xs"
              sx={(theme) => ({
                mb: theme.lelySpacing.SM,
                fontFamily: theme.lelyFontFamily.NOTO,
              })}
            >
              This functionality is used to upload diagnostic data from the
              Astronaut when needed.
              <br />
              A subset of the generated diagnostic data is stored locally in a
              circular data buffer instead of being sent directly to the cloud.
              <br />
              The buffer has a fixed capacity of 700 MB per device, allowing it
              to retain a limited history up to 7 days of robot activity.
              <br />
              When the buffer is full, the oldest data is automatically replaced
              by new incoming data. The exact length of the history retained in
              the buffer depends on the intensity of the robot&apos;s activities
              and the volume of diagnostic data generated during these
              activities.
            </Typography>

            <Typography
              variant="xs"
              sx={(theme) => ({
                fontFamily: theme.lelyFontFamily.NOTO,
                fontWeight: theme.typography.fontWeightBold,
              })}
            >
              Available options:
            </Typography>

            <Box
              sx={(theme) => ({
                gap: theme.lelySpacing.MD,
                display: 'flex',
              })}
            >
              {AVAILABLE_OPTIONS.map((option, index) => (
                <Box
                  key={option.title}
                  sx={(theme) => ({
                    flex: 1,
                    display: 'grid',
                    alignContent: 'start',
                    columnGap: theme.lelySpacing.XS,
                  })}
                >
                  <Typography
                    variant="xs"
                    sx={(theme) => ({
                      gridColumn: '1',
                      gridRow: '1',
                      fontFamily: theme.lelyFontFamily.NOTO,
                      fontWeight: theme.typography.fontWeightMedium,
                    })}
                  >
                    {`${index + 1}.`}
                  </Typography>
                  <Typography
                    variant="xs"
                    sx={(theme) => ({
                      gridColumn: '2',
                      gridRow: '1',
                      fontFamily: theme.lelyFontFamily.NOTO,
                      fontWeight: theme.typography.fontWeightMedium,
                    })}
                  >
                    {option.title}
                  </Typography>
                  <Typography
                    variant="xs"
                    sx={(theme) => ({
                      gridColumn: '2',
                      gridRow: '2',
                      fontFamily: theme.lelyFontFamily.NOTO,
                      fontWeight: theme.typography.fontWeightMedium,
                    })}
                  >
                    {option.description}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Stack>

          <Box
            sx={(theme) => ({
              display: 'flex',
              flexShrink: 0,
              padding: `${theme.lelySpacing.XS} ${theme.lelySpacing.SM}`,
              alignItems: 'center',
            })}
          >
            <RingBufferDiagram />
          </Box>
        </Box>
        <Typography
          variant="xs"
          sx={(theme) => ({ fontFamily: theme.lelyFontFamily.NOTO })}
        >
          For more details, see{' '}
          <Link
            href="https://lely.rightanswers.com/portal/app/portlets/results/viewsolution.jsp?solutionid=261006064151600"
            target="_blank"
            rel="noopener noreferrer"
            variant="inherit"
          >
            Knowledge base
          </Link>
          .
        </Typography>
      </Collapse>
    </Stack>
  );
};

export default RingBufferInfoSection;
